<template>
	<section class="irc" :aria-label="title">
		<header class="irc-head">
			<span class="irc-eyebrow">{{ title }}</span>
			<span v-if="whenLabel" class="irc-when" :title="whenFull">{{ whenLabel }}</span>
		</header>
		<dl class="irc-rows">
			<div v-for="row in visibleRows" :key="row.label" class="irc-row">
				<dt class="irc-label">{{ row.label }}</dt>
				<dd class="irc-value" :class="{ 'is-text': row.text }">{{ row.display }}</dd>
			</div>
			<div v-if="by" class="irc-row">
				<dt class="irc-label">Analista</dt>
				<dd class="irc-value is-text">{{ by }}</dd>
			</div>
			<div v-if="whenFull" class="irc-row">
				<dt class="irc-label">Fecha</dt>
				<dd class="irc-value">{{ whenFull }}</dd>
			</div>
		</dl>
	</section>
</template>

<script>
// Ficha de registro de la columna de contexto (última importación, última
// eliminación): eyebrow con el tick del panel + badge de frescura, y filas
// etiqueta/valor con hairlines — la misma receta que la ficha de Datos
// básicos. Genérico: recibe props, no conoce Vuex ni la API. Los valores
// numéricos salen con separador de miles; `text: true` marca los que no son
// cifras (duración, meses) para no darles peso de dato.
import { formatDateTimeEs, relativeDayLabel } from "@/utils/dateLabels";

export default {
	name: "ImportRecordCard",
	props: {
		title: { type: String, required: true },
		// ISO de cuándo ocurrió; alimenta el badge relativo y la fila Fecha.
		at: { type: String, default: "" },
		by: { type: String, default: "" },
		// [{ label, value, text? }] — las filas sin valor no se muestran.
		rows: { type: Array, default: () => [] },
	},
	computed: {
		visibleRows() {
			return this.rows
				.filter((row) => row.value !== null && row.value !== undefined && row.value !== "")
				.map((row) => ({
					...row,
					display:
						typeof row.value === "number" ? row.value.toLocaleString("es-CL") : row.value,
				}));
		},
		whenLabel() {
			return relativeDayLabel(this.at);
		},
		whenFull() {
			return formatDateTimeEs(this.at);
		},
	},
};
</script>

<style lang="scss" scoped>
.irc {
	margin-bottom: 12px;
}

.irc-head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
	margin-bottom: 4px;
}

// Mismo eyebrow con tick que el resto del panel: un solo gesto de acento.
.irc-eyebrow {
	display: flex;
	align-items: center;
	gap: 8px;
	min-width: 0;
	font-size: 11px;
	font-weight: 600;
	letter-spacing: 0.08em;
	text-transform: uppercase;
	color: var(--ap-muted);
}

.irc-eyebrow::before {
	content: "";
	flex: none;
	width: 2px;
	height: 10px;
	border-radius: 1px;
	background-color: var(--ap-teal);
}

// Frescura: lo primero que el analista busca ("¿está al día?").
.irc-when {
	flex: none;
	padding: 1px 8px;
	font-size: 10.5px;
	font-weight: 600;
	font-variant-numeric: tabular-nums;
	white-space: nowrap;
	color: var(--ap-teal-deep);
	background-color: var(--ap-teal-wash);
	border-radius: var(--ap-radius-pill);
}

.irc-rows {
	margin: 0;
}

.irc-row {
	display: flex;
	align-items: baseline;
	justify-content: space-between;
	gap: 12px;
	padding: 6px 0;
	border-top: 1px solid var(--ap-line);
}

.irc-row:last-child {
	border-bottom: 1px solid var(--ap-line);
}

.irc-label {
	flex: none;
	font-size: 12px;
	color: var(--ap-slate);
}

.irc-value {
	margin: 0;
	min-width: 0;
	font-size: 13px;
	font-weight: 600;
	font-variant-numeric: tabular-nums;
	text-align: right;
	overflow-wrap: anywhere;
	color: var(--ap-ink);
}

.irc-value.is-text {
	font-weight: 500;
	color: var(--ap-ink-2);
}
</style>
